/*
 * Purpose: Featured Flow Templates — dashboard hero + My flows inline strip
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { Link } from 'react-router-dom';
import { LayoutTemplate, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FlowTemplateQuickCard } from '@/components/flows/FlowTemplateQuickCard';
import {
  FLOW_TEMPLATES_IA,
  flowTemplateCount,
  getFeaturedFlowTemplates,
} from '@/lib/flowTemplates';
import { APP_ROUTES } from '@/lib/appRoutes';
import { cn } from '@/lib/utils';

export function FlowTemplatesSpotlight({
  variant = 'dashboard',
  className,
}: {
  variant?: 'dashboard' | 'inline';
  className?: string;
}) {
  const featured = getFeaturedFlowTemplates();
  const total = flowTemplateCount();

  if (variant === 'inline') {
    return (
      <Card
        className={cn(
          'p-4 sm:p-5 border-[hsl(var(--ab-green-teal)/0.25)] bg-gradient-to-br from-[hsl(var(--ab-green-teal)/0.06)] to-transparent',
          className
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <LayoutTemplate className="h-5 w-5 shrink-0 text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))] mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-foreground">{FLOW_TEMPLATES_IA.label}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {total} starter flows — clone in one click, then edit on the Mission map.
              </p>
            </div>
          </div>
        <Button asChild size="sm" actionTier="secondary" className="shrink-0">
          <Link to={APP_ROUTES.flowTemplates}>
            Browse all
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Link>
        </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {featured.map((template) => (
            <FlowTemplateQuickCard key={template.templateKey} template={template} />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <section className={cn('space-y-4', className)} aria-labelledby="flow-templates-heading">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2
            id="flow-templates-heading"
            className="text-lg font-semibold text-foreground flex items-center gap-2"
          >
            <LayoutTemplate className="h-5 w-5 text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))]" />
            {FLOW_TEMPLATES_IA.label}
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">{FLOW_TEMPLATES_IA.description}</p>
        </div>
        <Button asChild size="sm" actionTier="secondary" className="shrink-0">
          <Link to={APP_ROUTES.flowTemplates}>
            Browse {total} templates
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Link>
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {featured.map((template) => (
          <FlowTemplateQuickCard key={template.templateKey} template={template} />
        ))}
      </div>
    </section>
  );
}

export default FlowTemplatesSpotlight;
